import app from "@/main";
import {DosyaBorclu} from "../uyap/DosyaBorclu";
import {MessageLogger} from "../takip_ac/MessageLogger";
import {AvukatIcraTalepEvrakiGonderCevap} from "../uyap/AvukatIcraTalepEvrakiGonder";
import {saveAs} from "file-saver";
import {HacizGrupKoduEnum} from "../uyap/enum/HacizGrupKoduEnum";
import TakipDosya from "../uyap/classes/TakipDosya";
import {OdemeTipiEnum} from "@/plugins/uyap-plugin/takip_ac/OdemeTipleri";
import {durdurucuHataMi, uyapHataMesaji} from "../utils/tebligatTalepUtils";

/**
 * UYAP bir haciz talep evrakında en fazla 5 talep kabul ediyor; fazlası gönderimde
 * "tek seferde en fazla 5 adet haciz talebi eklenebilmektedir" (PRTL_GNL_1-1) ile reddediliyor.
 * Evrak hazırlama bu sınırı uygulamıyor, ret imzadan sonra geliyor (imza app HAR'ı, 02.10.2026).
 */
const EVRAK_BASINA_TALEP = 5;

/** Bir talebin evrak tutarına katkısı; ekleme metodlarının evrakTutar'a yazdığıyla aynı. */
const talepTutari = (talep: any): number => Number(talep.masraf ?? talep.talepMasrafi ?? 0);
const kurus = (tutar: any): number => Math.round(Number(tutar) * 100);

export default class HacizInjectable extends MessageLogger {
    talepBilgileri: any[] = [];
    /**
     * talepBilgileri ile aynı sırada, talebin görev verisindeki kaynağını gösteren anahtar.
     * Yarım kalan görev yeniden başlatıldığında UYAP'a gitmiş talepler bununla ayıklanır.
     */
    talepAnahtarlari: string[] = [];
    dosyaBloc: TakipDosya;
    borcluBloc: DosyaBorclu;
    evrakTutar: any = 0;
    id: number = 0;
    hacizEvrakImzaliBlob: Blob = new Blob();
    hacizEvrakImzasizBlob: Blob = new Blob();

    constructor(borclu: any, dosya: TakipDosya) {
        super();
        this.borcluBloc = borclu;
        this.dosyaBloc = dosya;
        this.talepBilgileri = [];
        this.evrakTutar = 0;
        this.id = 0;
    }

    pushTalepBilgileri(data: any) {
        data.id = this.id;
        this.talepBilgileri.push(data);
        this.id++;
    }

    /**
     * Talepleri sırayla en fazla EVRAK_BASINA_TALEP'lik parçalara ayırır; her parça ayrı evrak
     * olarak hazırlanıp imzalanır ve gönderilir. Sınırı aşmayan evrak olduğu gibi kalır.
     *
     * Portal böldüğü her evrakta talep id'lerini 0'dan başlatıyor (portal HAR'ı, 02.10.2026:
     * 14 gayrimenkul 5 + 5 + 4 olarak gitti); parçalarda da öyle.
     */
    parcala(): HacizInjectable[] {
        if (this.talepBilgileri.length <= EVRAK_BASINA_TALEP)
            return [this];
        const parcalar: HacizInjectable[] = [];
        for (let i = 0; i < this.talepBilgileri.length; i += EVRAK_BASINA_TALEP) {
            const parca = new HacizInjectable(this.borcluBloc, this.dosyaBloc);
            parca.talepBilgileri = this.talepBilgileri.slice(i, i + EVRAK_BASINA_TALEP)
                .map((talep, sira) => ({...talep, id: sira}));
            parca.talepAnahtarlari = this.talepAnahtarlari.slice(i, i + EVRAK_BASINA_TALEP);
            parca.evrakTutar = kurus(parca.talepBilgileri.reduce((toplam, talep) => toplam + talepTutari(talep), 0)) / 100;
            parcalar.push(parca);
        }
        // Parça tutarı talep nesnesinden türetiliyor. Ekleme metodlarının biriktirdiği toplamla
        // uyuşmuyorsa yanlış tutarla ödeme yapmaktansa hiçbir parça gönderilmez.
        const parcaToplami = parcalar.reduce((toplam, parca) => toplam + kurus(parca.evrakTutar), 0);
        if (parcaToplami != kurus(this.evrakTutar))
            throw new Error("Evrak tutarı parçalara dağıtılamadı (evrak: " + (kurus(this.evrakTutar) / 100).toFixed(2) +
                " TL, parçalar: " + (parcaToplami / 100).toFixed(2) + " TL). Talepler gönderilmedi.");
        return parcalar;
    }

    /**
     * Evrak hazırlanıp imzalanmadan önce e-barobirlik bakiyesini talep tutarıyla karşılaştırır.
     * Talep gönderiminde UYAP, bakiye yetersizliğini ayrı bir mesajla bildirmiyor; genel
     * "İşlem Başarısız. Sistem yöneticisine bildiriniz." (PRTL_GNL_100012) dönüyor. UYAP
     * portalı da aynı hesapla aynı hatayı aldı (DEV-3519 test HAR'ı, 01.10.2026). Kullanıcıya
     * yol gösteren mesaj bu yüzden burada üretiliyor.
     *
     * Bakiye her dosyada yeniden sorgulanıyor: aynı paketteki her gönderim bakiyeyi düşürüyor.
     * Sorgu ücretsiz ve beklemesiz. Bakiye okunamazsa kontrol atlanır ve gönderim eskisi
     * gibi sürer. Durdurma, devre kesici ve kimlik hataları yutulmaz.
     */
    async bakiyeyiKontrolEt(): Promise<void> {
        const tutar = Number(this.evrakTutar);
        if (!(tutar > 0))
            return;
        let bakiye = NaN;
        try {
            const cevap = await app.$uyap.AvukatBakiyeSorgula().run({});
            if (cevap && cevap.type === "success" && cevap.value !== null && String(cevap.value).trim() !== "")
                bakiye = Number(cevap.value);
        } catch (e) {
            if (durdurucuHataMi(e))
                throw e;
            console.warn("E-barobirlik bakiyesi sorgulanamadı, bakiye kontrolü atlanıyor:", uyapHataMesaji(e));
            return;
        }
        if (!isFinite(bakiye)) {
            console.warn("E-barobirlik bakiyesi okunamadı, bakiye kontrolü atlanıyor.");
            return;
        }
        // Kuruş düzeyinde karşılaştırma; kayan nokta artıkları (418.70000001) yanlış sonuç vermesin.
        if (Math.round(bakiye * 100) < Math.round(tutar * 100)) {
            throw new Error("E-barobirlik bakiyeniz bu talep için yetersiz (bakiye: " + bakiye.toFixed(2) +
                " TL, talep tutarı: " + tutar.toFixed(2) + " TL). Bakiye yükledikten sonra talebi yeniden gönderiniz.");
        }
    }

    hacizEvrakHazirla(): Promise<void> {
        console.log("TALEP BILGILERI,", this.talepBilgileri);
        return new Promise(async (resolve, reject) => {
            try {
                this.addMessage("info", "Haciz Evrak Hazırlanıyor..");
                this.hacizEvrakImzasizBlob = await app.$uyap.IcraTalepEvrakHazirla().run({
                        dosyaId: this.dosyaBloc.dosyaId,
                        dosyaEsasNo: '',
                        filename: this.dosyaBloc.dosyaNo,
                        kisiKurumId: this.borcluBloc.kisiKurumId,
                        talepBilgileri: JSON.stringify(this.talepBilgileri)
                });
                this.addMessage("success", "Haciz evrak imzasız blob hazırlandı..");
                return resolve();
            } catch (e) {
                this.addMessage("error", "Haciz evrak imzasız blob HATA :" + e.message);
                return reject(e);
            }
        })
    }

    hacizEvrakImzala(): Promise<void> {
        return new Promise(async (resolve, reject) => {
            try {
                this.addMessage("info", "Haciz Evrak İmzalanıyor...");
                let b64Udf = await app.$imza.dokumanImzala(this.hacizEvrakImzasizBlob);
                this.hacizEvrakImzaliBlob = app.$uyapHelper.base64toBlob(b64Udf, "application/octet-stream");
                (this.hacizEvrakImzaliBlob as any).name = (this.hacizEvrakImzasizBlob as any).name;
                //saveAs(this.hacizEvrakImzaliBlob, "imzali.udf");
                //saveAs(this.hacizEvrakImzasizBlob, "imzasiz.udf");
                this.addMessage("success", "Haciz Evrak İmzalandı...");
                return resolve();
            } catch (e) {
                this.addMessage("info", "Haciz Evrak İmzalama HATA *" + e.toString());
                return reject(e);
            }
        })
    }

    hacizEvrakIndir() {
        saveAs(this.hacizEvrakImzasizBlob, "HazirlananEvrak.udf");
    }

    gonder(): Promise<AvukatIcraTalepEvrakiGonderCevap> {
        console.log("HACİZ EVRAK::gonder");
        return new Promise(async (resolve, reject) => {
            try {
                this.addMessage("info", "Haciz Evrak Gönderiliyor..");
                let form = new FormData();
                // replace('/','_') sadece ILK slash'i degistiriyordu. UYAP dosyaNo'yu
                // "2020/5687 - ( 2018/2275 )" gibi iki slash'li de dondurebiliyor; bu durumda
                // yuklenen dosya adinda slash kaliyor ve multipart filename'de yol ayraci
                // olarak yorumlanabiliyor. (DEV-1131)
                let dosyaAdi = this.dosyaBloc.dosyaNo.replace(/\//g, '_') + "_imzali.udf";
                let hacizTalepItems = [
                    {
                        "id": 1,
                        "evrakTuruOptionDVO": {
                            "tur": "ICR_AVUKAT_PORTAL_HACIZ_TALEP",
                            "id": "ICR_AVUKAT_PORTAL_HACIZ_TALEP",
                            "label": "Haciz Talebi",
                            "mandatory": true,
                            "max": 1,
                            "ekEvrakMax": 5
                        },
                        "tur": "ICR_AVUKAT_PORTAL_HACIZ_TALEP",
                        "turAciklama": "Haciz Talebi",
                        "mandatory": true,
                        "file": {},
                        "path": "C:\\fakepath\\" + dosyaAdi,
                        "kullaniciEvrakAciklama": "Haciz Talebi",
                        "parentId": -1,
                        "fileId": 1
                    }
                ];
                let genelTalepItems = [{
                    "id": 1,
                    "evrakTuruOptionDVO": {
                        "tur": "ICR_AVUKAT_PORTAL_GENEL_TALEP",
                        "id": "ICR_AVUKAT_PORTAL_GENEL_TALEP",
                        "label": "Diğer Talebi",
                        "mandatory": true,
                        "max": 1,
                        "ekEvrakMax": 5
                    },
                    "tur": "ICR_AVUKAT_PORTAL_GENEL_TALEP",
                    "turAciklama": "Diğer Talebi",
                    "mandatory": true,
                    "file": {},
                    "path": "C:\\fakepath\\" + dosyaAdi,
                    "kullaniciEvrakAciklama": "Diğer Talebi",
                    "parentId": -1,
                    "fileId": 1
                }];

                let tebligatTalepItems = [{
                    "id": 1,
                    "evrakTuruOptionDVO": {
                        "tur": "ICR_AVUKAT_PORTAL_TEBLIGAT_TALEP",
                        "id": "ICR_AVUKAT_PORTAL_TEBLIGAT_TALEP",
                        "label": "Tebligat Talebi",
                        "mandatory": true,
                        "max": 1,
                        "ekEvrakMax": 5
                    },
                    "tur": "ICR_AVUKAT_PORTAL_TEBLIGAT_TALEP",
                    "turAciklama": "Tebligat Talebi",
                    "mandatory": true,
                    "file": {},
                    "path": "C:\\fakepath\\" + dosyaAdi,
                    "kullaniciEvrakAciklama": "Tebligat Talebi",
                    "parentId": -1,
                    "fileId": 1
                }]

                let selectedGrupKodu = this.talepBilgileri[0].grupKodu;
                let selectedTalepItems = {};

                if (selectedGrupKodu == HacizGrupKoduEnum.Haciz)
                    selectedTalepItems = hacizTalepItems;
                if (selectedGrupKodu == HacizGrupKoduEnum.Tebligat)
                    selectedTalepItems = tebligatTalepItems;
                if (selectedGrupKodu == HacizGrupKoduEnum.Genel)
                    selectedTalepItems = genelTalepItems;

                form.append('file_1', this.hacizEvrakImzaliBlob, dosyaAdi);
                form.append('items', JSON.stringify(selectedTalepItems));
                form.append('dosyaId', this.dosyaBloc.dosyaId);
                form.append('kisiKurumId', this.borcluBloc.kisiKurumId.toString());
                form.append('tutar', parseFloat(this.evrakTutar).toFixed(2));
                form.append('talepGrupId', this.talepBilgileri[0].grupKodu);
                if (selectedGrupKodu == HacizGrupKoduEnum.Genel) {
                    // @ts-ignore
                    form.append('avansTutariDusulsunMu', false);
                }
                form.append('talepBilgileri', JSON.stringify(this.talepBilgileri));
                console.log("before evrak tutar", this.evrakTutar);
                if (this.evrakTutar > 0) {
                    form.append('vakifbankHesapBilgileri', 'null');
                    form.append('vakifbankOdemeIstekBilgileri', 'null');
                    form.append('smsSifre', 'null');
                    form.append('odemeTipi', OdemeTipiEnum.Ebarobirlikkart);
                }

                let result = await app.$uyap.AvukatIcraTalepEvrakiGonder().run({
                    formData: true,
                    data: form
                })
                this.addMessage("success", "Haciz Evrak Gönderimi Başarılı..");
                return resolve(result);
            } catch (e) {
                this.addMessage("error", "Haciz Evrak Gönderimi Hatalı. Hata :" + e.message);
                return reject(e);
            }
        })

    }

    getHacizEvrakImzasiz() {
        return this.hacizEvrakImzasizBlob;
    }

    getHacizEvrakImzali() {
        return this.hacizEvrakImzaliBlob;
    }
}
